import { useMemo, useState, useEffect } from 'react';
import { checkWaitlistStatus } from '../services/waitlist';
import { DEFAULT_TRIAL_DURATION_DAYS, WAITLIST_TRIAL_DURATION_DAYS } from '../constants/trial';

/**
 * Custom hook to calculate trial status for a user
 * @param {Object} userInfo - User information from backend
 * @returns {Object} Trial status information
 */
export const useTrialStatus = (userInfo) => {
  const [isWaitlistUser, setIsWaitlistUser] = useState(false);
  const [waitlistCheckComplete, setWaitlistCheckComplete] = useState(false);

  // Check if user is on waitlist when userInfo changes
  useEffect(() => {
    const checkUserWaitlistStatus = async () => {
      if (userInfo?.email) {
        try {
          const isOnWaitlist = await checkWaitlistStatus(userInfo.email);
          setIsWaitlistUser(isOnWaitlist);
        } catch (error) {
          console.error('Error checking waitlist status:', error);
          setIsWaitlistUser(false);
        }
      } else {
        setIsWaitlistUser(false);
      }
      setWaitlistCheckComplete(true);
    };

    if (userInfo) {
      checkUserWaitlistStatus();
    } else {
      setWaitlistCheckComplete(true);
    }
  }, [userInfo?.email]);

  const trialDurationDays = isWaitlistUser ? WAITLIST_TRIAL_DURATION_DAYS : DEFAULT_TRIAL_DURATION_DAYS;
  return useMemo(() => {
    // Don't calculate until waitlist check is complete
    if (!waitlistCheckComplete) {
      return {
        isTrialUser: false,
        daysRemaining: 0,
        isTrialExpired: false,
        trialEndDate: null,
        isPaidUser: false,
        canUseChat: false,
        isWaitlistUser: false,
        trialDurationDays: DEFAULT_TRIAL_DURATION_DAYS,
        loading: true
      };
    }

    // Default values if no user info
    if (!userInfo) {
      return {
        isTrialUser: false,
        daysRemaining: 0,
        isTrialExpired: false,
        trialEndDate: null,
        isPaidUser: false,
        canUseChat: false,
        isWaitlistUser: false,
        trialDurationDays: DEFAULT_TRIAL_DURATION_DAYS,
        loading: false
      };
    }

    // Check if user has active subscription
    // Subscription data is stored directly on the user object
    const hasActiveSubscription = userInfo.subscriptionStatus === 'active' && 
      userInfo.paymentStatus === 'paid';

    if (hasActiveSubscription) {
      return {
        isTrialUser: false,
        daysRemaining: 0,
        isTrialExpired: false,
        trialEndDate: null,
        isPaidUser: true,
        canUseChat: true,
        isWaitlistUser,
        trialDurationDays,
        loading: false
      };
    }

    // Calculate trial status for non-paid users
    const createdAt = new Date(userInfo.createdAt);
    const now = new Date();
    const daysSinceCreation = Math.floor((now - createdAt) / (1000 * 60 * 60 * 24));
    const daysRemaining = Math.max(0, trialDurationDays - daysSinceCreation);
    const isTrialExpired = daysRemaining === 0;
    
    // Calculate trial end date
    const trialEndDate = new Date(createdAt);
    trialEndDate.setDate(trialEndDate.getDate() + trialDurationDays);

    return {
      isTrialUser: true,
      daysRemaining,
      isTrialExpired,
      trialEndDate,
      isPaidUser: false,
      canUseChat: !isTrialExpired,
      isWaitlistUser,
      trialDurationDays,
      loading: false
    };
  }, [userInfo, waitlistCheckComplete, isWaitlistUser, trialDurationDays]);
};
